import { Collapse, Flex, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import type { IPublicCourseResponse } from "../../../app/features/courses/courses.types";
import BottomDropdownIcon from "../../../assets/icons/BottomDropdownIcon";
import QuizLabelIcon from "../../../pages/user/courseModule/_icons/QuizLabelIcon";
import VideoLabelIcon from "../../../pages/user/courseModule/_icons/VideoLabelIcon";
import { cn } from "../../../utils/global.utils";
import CourseCurriculumLoading from "./CourseCurriculumLoading";

const { Panel } = Collapse;
const { Text } = Typography;

type Props = {
  course?: IPublicCourseResponse;
  isLoading?: boolean;
};

export default function CourseCurriculum({ course, isLoading }: Props) {
  const [activeKey, setActiveKey] = useState<string | string[]>([]);

  const sections = useMemo(() => {
    return (
      course?.CourseSection?.map((section) => {
        const lectures =
          section.Lecture?.map((l) => ({
            id: l.id,
            title: l.title,
            durationSec: l.durationSec || 0,
            contentType: l.contentType?.toLowerCase(),
            isPreview: l.isPreview,
          })) || [];

        const quizzes =
          section.Quiz?.map((q) => ({
            id: q.id,
            title: q.title,
            durationSec: 0,
            contentType: "quiz",
            isPreview: false,
          })) || [];

        return {
          id: section.id,
          title: section.title,
          lectureCount: lectures.length,
          duration: section.duration, // This comes as a number (minutes?) or string from API? Type says number.
          content: [...lectures, ...quizzes],
        };
      }) || []
    );
  }, [course]);

  useEffect(() => {
    if (sections?.length > 0) {
      setActiveKey([sections[0].id.toString()]);
    }
  }, [sections]);

  const handleChange = (key: string | string[]) => {
    setActiveKey(key);
  };

  if (isLoading) {
    return <CourseCurriculumLoading />;
  }

  return (
    <div className="rounded-lg shadow-sm bg-theme-slate-25 border border-theme-zinc-200">
      <Collapse
        accordion={true}
        activeKey={activeKey}
        onChange={handleChange}
        expandIcon={({ isActive }) => (
          <BottomDropdownIcon
            className={cn(
              "transition-transform",
              isActive
                ? "rotate-180 !text-theme-primary"
                : "rotate-0 !text-theme-gray-500",
            )}
          />
        )}
        className="bg-white border-0 course-module"
        bordered={false}
      >
        {sections.map((section) => (
          <Panel
            header={
              <Flex
                justify="space-between"
                className="w-full pr-4 flex-col sm:flex-wrap items-start sm:items-center sm:flex-row"
              >
                <Text
                  className={cn(
                    "text-theme-slate-700 text-base sm:text-lg font-medium font-primary transition-all hover:text-theme-primary",
                    section.id.toString() === activeKey[0] &&
                      "text-theme-primary",
                  )}
                >
                  {section.title}
                </Text>
                <Text className="text-theme-slate-600 text-xs lg:text-sm font-roboto w-full lg:w-auto text-right">
                  {section.lectureCount || 0}{" "}
                  {section.lectureCount > 1 ? "Lectures" : "Lecture"}
                  {/* •{" "}
                  {Math.floor(section.duration / 60)}h {section.duration % 60}m */}
                </Text>
              </Flex>
            }
            key={section.id}
            className="border-b border-gray-200 last:border-b-0 course-list course-module-list"
          >
            <div className="pl-6 pr-4 pb-2">
              <ul className="space-y-2">
                {section.content?.map((item: any) => (
                  <li
                    key={`${item.contentType}-${item.id}`}
                    className="flex items-center gap-3 py-2 rounded transition-colors"
                  >
                    <Flex align="center" justify="center" className="w-4">
                      {item.contentType === "video" ? (
                        <VideoLabelIcon className="text-theme-slate-500" />
                      ) : item.contentType === "quiz" ? (
                        <QuizLabelIcon className="text-theme-slate-500 relative -top-[1px]" />
                      ) : (
                        <span className="size-2 bg-theme-zinc-700 rounded-full relative -top-[1px] inline-block" />
                      )}
                    </Flex>
                    <div className="flex-1 min-w-0">
                      <p className="text-theme-slate-700 text-sm font-medium truncate">
                        {item?.contentType === "quiz" ? "Quiz" : item.title}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-theme-slate-500">
                      {item.contentType === "video" && (
                        <>
                          {/* {item.isPreview && (
                            <span className="text-theme-orange-600 text-xs underline">
                              Preview
                            </span>
                          )} */}
                          <span className="md:w-16 text-right text-theme-slate-500 text-sm font-normal">
                            {(() => {
                              const hours = Math.floor(item.durationSec / 3600);
                              const minutes = Math.floor(
                                (item.durationSec % 3600) / 60,
                              );
                              const seconds = item.durationSec % 60;

                              const paddedMinutes = minutes
                                .toString()
                                .padStart(2, "0");
                              const paddedSeconds = seconds
                                .toString()
                                .padStart(2, "0");

                              if (hours > 0) {
                                const paddedHours = hours
                                  .toString()
                                  .padStart(2, "0");
                                return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
                              }
                              return `${paddedMinutes}:${paddedSeconds}`;
                            })()}
                          </span>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Panel>
        ))}
      </Collapse>
    </div>
  );
}
